// ===========================================
// BUSTED! BLACKJACK - TABLEMASTER TYPES & VALIDATION
// ===========================================
// Shared types and validation logic for ServerTableMaster and ClientTableMaster.
// These enforce keyed event sequencing to prevent out-of-order event processing.
//
// See: docs/TABLEMASTER_DESIGN.md
// ===========================================
import { GamePhase } from './enums.js';
// ===========================================
// VALIDATION FUNCTIONS
// ===========================================
/**
 * Valid phase transitions in the game flow.
 * Key = current phase, Value = array of valid next phases.
 */
const VALID_PHASE_TRANSITIONS = {
    [GamePhase.WAITING]: [GamePhase.WAITING, GamePhase.BETTING],
    [GamePhase.BETTING]: [GamePhase.BETTING, GamePhase.DEALING],
    [GamePhase.DEALING]: [GamePhase.DEALING, GamePhase.PLAYING, GamePhase.DEALER_TURN, GamePhase.RESOLVING],
    [GamePhase.PLAYING]: [GamePhase.PLAYING, GamePhase.DEALER_TURN, GamePhase.RESOLVING],
    [GamePhase.DEALER_TURN]: [GamePhase.DEALER_TURN, GamePhase.RESOLVING],
    [GamePhase.RESOLVING]: [GamePhase.RESOLVING, GamePhase.WAITING, GamePhase.BETTING],
};
/**
 * Check if a phase transition is valid.
 */
export function isValidPhaseTransition(from, to) {
    const validTargets = VALID_PHASE_TRANSITIONS[from];
    return validTargets?.includes(to) ?? false;
}
/**
 * Validate an event key against current state.
 * Used by both server (before sending) and client (before processing).
 */
export function validateEventKey(incoming, state) {
    // Round validation
    if (incoming.round < state.currentRound) {
        return {
            valid: false,
            reason: 'STALE_ROUND',
            message: `Event round ${incoming.round} is older than current round ${state.currentRound}`,
        };
    }
    if (incoming.round > state.currentRound + 1) {
        return {
            valid: false,
            reason: 'FUTURE_ROUND',
            message: `Event round ${incoming.round} is too far ahead of current round ${state.currentRound}`,
        };
    }
    // If same round, validate phase transition
    if (incoming.round === state.currentRound) {
        if (!isValidPhaseTransition(state.currentPhase, incoming.phase)) {
            return {
                valid: false,
                reason: 'INVALID_PHASE',
                message: `Invalid phase transition from ${state.currentPhase} to ${incoming.phase}`,
            };
        }
        // Validate sequence (must be greater than last accepted)
        if (incoming.sequence <= state.lastEventSequence) {
            if (incoming.sequence === state.lastEventSequence) {
                return {
                    valid: false,
                    reason: 'DUPLICATE_SEQUENCE',
                    message: `Duplicate sequence number ${incoming.sequence}`,
                };
            }
            return {
                valid: false,
                reason: 'STALE_SEQUENCE',
                message: `Sequence ${incoming.sequence} <= last processed ${state.lastEventSequence}`,
            };
        }
    }
    // New round (round + 1) - sequence should reset, phase should be BETTING or WAITING
    if (incoming.round === state.currentRound + 1) {
        if (incoming.phase !== GamePhase.BETTING && incoming.phase !== GamePhase.WAITING) {
            return {
                valid: false,
                reason: 'INVALID_PHASE',
                message: `New round should start with BETTING or WAITING, got ${incoming.phase}`,
            };
        }
    }
    return { valid: true };
}
/**
 * Validate a batch key against current state.
 */
export function validateBatchKey(incoming, state) {
    // Round validation (same as event)
    if (incoming.round < state.currentRound) {
        return {
            valid: false,
            reason: 'STALE_ROUND',
            message: `Batch round ${incoming.round} is older than current round ${state.currentRound}`,
        };
    }
    if (incoming.round > state.currentRound + 1) {
        return {
            valid: false,
            reason: 'FUTURE_ROUND',
            message: `Batch round ${incoming.round} is too far ahead of current round ${state.currentRound}`,
        };
    }
    // If same round, validate batch sequence
    if (incoming.round === state.currentRound) {
        if (incoming.batchSequence <= state.lastBatchSequence) {
            if (incoming.batchSequence === state.lastBatchSequence) {
                return {
                    valid: false,
                    reason: 'DUPLICATE_SEQUENCE',
                    message: `Duplicate batch sequence ${incoming.batchSequence}`,
                };
            }
            return {
                valid: false,
                reason: 'STALE_SEQUENCE',
                message: `Batch sequence ${incoming.batchSequence} <= last processed ${state.lastBatchSequence}`,
            };
        }
        // Validate phase transition
        if (!isValidPhaseTransition(state.currentPhase, incoming.phase)) {
            return {
                valid: false,
                reason: 'INVALID_PHASE',
                message: `Invalid phase transition from ${state.currentPhase} to ${incoming.phase}`,
            };
        }
    }
    return { valid: true };
}
/**
 * Validate that all events in a batch have keys consistent with the batch key.
 */
export function validateBatchConsistency(batch) {
    for (const event of batch.events) {
        // Event round must match batch round
        if (event.key.round !== batch.key.round) {
            return {
                valid: false,
                reason: 'BATCH_MISMATCH',
                message: `Event round ${event.key.round} doesn't match batch round ${batch.key.round}`,
            };
        }
    }
    // Events within batch should be in sequence order
    let lastSeq = -1;
    for (const event of batch.events) {
        if (event.key.sequence <= lastSeq) {
            return {
                valid: false,
                reason: 'STALE_SEQUENCE',
                message: `Events within batch are not in sequence order`,
            };
        }
        lastSeq = event.key.sequence;
    }
    return { valid: true };
}
// ===========================================
// STATE MANAGEMENT HELPERS
// ===========================================
/**
 * Create initial TableMaster state.
 */
export function createInitialTableMasterState() {
    return {
        currentRound: 0,
        currentPhase: GamePhase.WAITING,
        lastEventSequence: 0,
        lastBatchSequence: 0,
        currentTurnSeat: undefined,
    };
}
/**
 * Update state after accepting an event.
 * Returns new state (immutable update).
 */
export function updateStateAfterEvent(state, eventKey) {
    return {
        ...state,
        currentRound: eventKey.round,
        currentPhase: eventKey.phase,
        lastEventSequence: eventKey.sequence,
        // Update turn seat if this is a seat-specific event in PLAYING phase
        currentTurnSeat: eventKey.phase === GamePhase.PLAYING && eventKey.seat !== undefined
            ? eventKey.seat
            : state.currentTurnSeat,
    };
}
/**
 * Update state after accepting a batch.
 * Returns new state (immutable update).
 */
export function updateStateAfterBatch(state, batchKey, lastEventKey) {
    return {
        ...state,
        currentRound: batchKey.round,
        currentPhase: batchKey.phase,
        lastBatchSequence: batchKey.batchSequence,
        lastEventSequence: lastEventKey?.sequence ?? state.lastEventSequence,
        currentTurnSeat: lastEventKey?.phase === GamePhase.PLAYING && lastEventKey?.seat !== undefined
            ? lastEventKey.seat
            : state.currentTurnSeat,
    };
}
/**
 * Reset state for a new round.
 */
export function resetStateForNewRound(state, newRoundNumber) {
    return {
        currentRound: newRoundNumber,
        currentPhase: GamePhase.BETTING,
        lastEventSequence: 0,
        lastBatchSequence: 0,
        currentTurnSeat: undefined,
    };
}
// ===========================================
// KEY GENERATION HELPERS
// ===========================================
/**
 * Create an event key.
 */
export function createEventKey(round, phase, sequence, seat) {
    return { round, phase, sequence, seat };
}
/**
 * Create a batch key.
 */
export function createBatchKey(round, phase, batchSequence) {
    return { round, phase, batchSequence };
}
/**
 * Format an event key as a string for logging.
 * Format: "R{round}.{phase}.S{seat}.{sequence}" or "R{round}.{phase}.__.{sequence}"
 */
export function formatEventKey(key) {
    const seatPart = key.seat !== undefined ? `S${key.seat}` : '__';
    return `R${key.round}.${key.phase}.${seatPart}.${String(key.sequence).padStart(3, '0')}`;
}
/**
 * Format a batch key as a string for logging.
 * Format: "R{round}.{phase}.B{batchSequence}"
 */
export function formatBatchKey(key) {
    return `R${key.round}.${key.phase}.B${String(key.batchSequence).padStart(3, '0')}`;
}
// ===========================================
// BALANCE DERIVATION HELPERS
// ===========================================
/**
 * Event types that affect player balance (outgoing - decrements balance).
 */
export const BALANCE_DECREMENT_EVENTS = [
    'BET_CONFIRMED',
    'SIDEBET_PLACED',
    'INSURANCE_DECISION', // When accepted
];
/**
 * Event types that affect player balance (incoming - increments balance).
 */
export const BALANCE_INCREMENT_EVENTS = [
    'CHIPS_PAID',
    'CHIPS_RETURNED',
    'SIDEBET_CHIPS_PAID',
    'INSURANCE_RESOLVED', // When dealer has blackjack and player wins insurance
];
/**
 * Check if an event type affects balance.
 */
export function isBalanceAffectingEvent(eventType) {
    return (BALANCE_DECREMENT_EVENTS.includes(eventType) ||
        BALANCE_INCREMENT_EVENTS.includes(eventType));
}
/**
 * Determine balance delta direction for an event type.
 * Returns 1 for increment, -1 for decrement, 0 for no effect.
 */
export function getBalanceDeltaDirection(eventType) {
    if (BALANCE_INCREMENT_EVENTS.includes(eventType))
        return 1;
    if (BALANCE_DECREMENT_EVENTS.includes(eventType))
        return -1;
    return 0;
}
//# sourceMappingURL=tablemaster.js.map